//—— 命書的章節目錄 ——
//
//  一章一張卡，點了直接跳過去。
//
//  —— 每張卡顯示的是「這一章你讀到哪了」——
//  不是章首頁。這是整個目錄最有用的地方：九章六萬字不會一次讀完，
//  回來時第一個問題永遠是「我讀到哪了」。卡片直接回答它，
//  使用者不必自己翻找。
//
//  進度看 visited（翻開過哪些頁）而不是 index（現在在哪一頁）。
//  只看 index 的話同時間只有一章有進度 —— 讀完第一章進了第二章，
//  第一章就又變回「尚未開始」，那不叫進度。
//
//  也不能用「讀到最遠哪一頁」來算：目錄可以直接跳章，
//  從第一章跳到第五章，中間三章會全部被算成讀完 —— 它們連開都沒開過。
//  一節一節數才是真的。
//
//  props:
//    chapters  章節表（API 來的）
//    pages     攤平後的全部頁（含封面）
//    index     目前在第幾頁
//    visited   翻開過的頁碼（Set）
//    onPick    點某一章 → 跳到該章該接下去的那一頁
//    onClose   關閉

//某一章在攤平陣列裡的範圍：[章首頁, 最後一個小節頁]
const rangeOf = (pages, chKey) => {
  const from = pages.findIndex(
    (p) => p.type === "chapter" && p.ch.key === chKey,
  );
  if (from < 0) return null;
  let to = from;
  while (to + 1 < pages.length && pages[to + 1].type === "section") to += 1;
  return { from, to };
};

const FeihongReportIndex = ({
  chapters,
  pages,
  index,
  visited,
  onPick,
  onClose,
}) => {
  const read = visited || new Set();
  //收尾頁在攤平陣列裡的位置。沒有就不畫那張卡
  const outroAt = pages.findIndex((p) => p.type === "outro");

  return (
  <div className="fri" role="dialog" aria-modal="true" aria-label="章節目錄">
    <div className="fri__mask" onClick={onClose} />

    <div className="fri__panel">
      <div className="fri__head">
        <h3 className="fri__title">章節目錄</h3>
        <button
          type="button"
          className="fri__close"
          onClick={onClose}
          aria-label="關閉"
        >
          ✕
        </button>
      </div>

      <div className="fri__grid">
        {chapters.map((ch) => {
          const range = rangeOf(pages, ch.key);
          //內容缺這一章就不畫卡 —— 點下去會跳到不存在的頁
          if (!range) return null;

          const here = index >= range.from && index <= range.to;

          //這一章的小節頁。章首頁（range.from）不算 ——
          //  它沒有內容，翻到章首頁就說「讀了 1/3」會高估。
          const secs = [];
          for (let i = range.from + 1; i <= range.to; i += 1) secs.push(i);

          //真的翻開過的那幾節
          const seen = secs.filter((i) => read.has(i));
          const done = secs.length > 0 && seen.length === secs.length;
          //翻開過的最後一節，卡片上寫它的標題
          const last = seen.length ? seen[seen.length - 1] : -1;

          //點下去跳哪一頁：
          //    正在讀這一章 → 就是現在這一頁（等於不動，但會關掉目錄）
          //    整章讀完了   → 回章首頁，這時候點進來是要重讀
          //    讀了一半     → 第一個還沒翻開的那一節，接著讀
          //    還沒開始     → 章首頁
          const next = secs.find((i) => !read.has(i));
          const at = here
            ? index
            : done || seen.length === 0
              ? range.from
              : next;

          return (
            <button
              type="button"
              key={ch.key}
              className={`fri__card${here ? " is-here" : ""}`}
              onClick={() => onPick(at)}
            >
              {/* badge（「第一卷」）不是 no —— 緋紅算的是卷。
                  六卷與七卷兩種都要畫得出來，不要寫死。 */}
              <span className="fri__card-no">{ch.badge}</span>
              <span className="fri__card-title">{ch.title}</span>

              {/* 縮圖的替代品：直接寫「讀到哪一節」。
                  真的畫一張縮圖要把那一頁縮小渲染一次，九章就是九份長文
                  同時掛在 DOM 上 —— 為了一張看不清楚的小圖不值得。 */}
              <span className="fri__card-at">
                {done
                  ? "已讀完"
                  : last < 0
                    ? "尚未開始"
                    : pages[last]?.sec?.title}
              </span>

              <span className="fri__card-bar" aria-hidden="true">
                <i
                  style={{
                    width: `${
                      secs.length ? (seen.length / secs.length) * 100 : 0
                    }%`,
                  }}
                />
              </span>
            </button>
          );
        })}

        {/* 收尾頁。它不是一章，但在目錄裡要看得到 ——
            讀者想回去留評價時，唯一的入口就是這裡（翻 37 頁太遠）。 */}
        {outroAt >= 0 && (
          <button
            type="button"
            className={`fri__card${index === outroAt ? " is-here" : ""}`}
            onClick={() => onPick(outroAt)}
          >
            <span className="fri__card-no">最後</span>
            <span className="fri__card-title">留下你的話</span>
            <span className="fri__card-at">評價・聯絡客服</span>

            {/* 這一張只有一頁，沒有「讀到幾成」—— 到過就是滿格。
                不畫的話九張卡有進度條、第十張沒有，看起來像壞掉。 */}
            <span className="fri__card-bar" aria-hidden="true">
              <i style={{ width: read.has(outroAt) ? "100%" : "0%" }} />
            </span>
          </button>
        )}
      </div>
      </div>
    </div>
  );
};

export default FeihongReportIndex;
